/**
 * unreadFloor — persistent per-(user, room) unread count floor (NED-3439).
 *
 * matrix-js-sdk rebuilds `notification_count` for encrypted rooms one event
 * at a time via `fixNotificationCountOnDecryption`, so with
 * `initialSyncLimit: 10` the first paint after a refresh can show a
 * count bounded by the decryption crawl rather than the real unread state.
 *
 * This module persists the last known *trusted* count per room to
 * `localStorage`, so `resolveUnreadCount` can return a value no lower than
 * what was reliably observed before the refresh while the marker backfill
 * is in flight.
 *
 * Storage shape:
 *   key:   tim:unread-floor:<userId>
 *   value: { v: 1, rooms: { [roomId]: { count, ts, markerId } } }
 *
 * `count` is the unread count observed at a trusted resolution; `ts` is
 * `Date.now()` at write time; `markerId` is the effective read marker
 * event id used to compute `count`.
 *
 * Reads go through `getFloor` (or `getFloorEntry` when the caller needs
 * `markerId` / `ts` for staleness comparisons). Reads return `undefined`
 * when:
 *   - the kill switch `window.__disableUnreadFloor` is truthy,
 *   - storage is unavailable or the schema doesn't match,
 *   - the per-session `inactiveRooms` set has marked this (user, room)
 *     inactive — e.g. after a receipt, a room-level `m.fully_read`
 *     change, or a membership leave.
 *
 * Writes go through `setFloor` and are guarded against cross-tab staleness:
 * if another tab wrote a newer `ts` with a different count since we last
 * loaded, we yield to that value rather than overwriting.
 */

const KEY_PREFIX = 'tim:unread-floor:';
const SCHEMA_VERSION = 1;
const MAX_REASONABLE_COUNT = 10_000;

// userId → Map<roomId, { count, ts, markerId }>
const cache = new Map();

// Per-session set of rooms whose floor should not be returned until a
// trusted resolution writes a fresh value (or the session reloads).
// userId → Set<roomId>
const inactiveRooms = new Map();

function isDisabled() {
  try {
    return typeof window !== 'undefined' && Boolean(window.__disableUnreadFloor);
  } catch {
    return false;
  }
}

function getStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function storageKey(userId) {
  return `${KEY_PREFIX}${userId}`;
}

function parseStorage(userId) {
  const storage = getStorage();
  if (!storage) return new Map();
  let raw;
  try {
    raw = storage.getItem(storageKey(userId));
  } catch {
    return new Map();
  }
  if (!raw) return new Map();

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return new Map();
  }

  if (!parsed || parsed.v !== SCHEMA_VERSION || !parsed.rooms || typeof parsed.rooms !== 'object') {
    return new Map();
  }

  const m = new Map();
  for (const [roomId, entry] of Object.entries(parsed.rooms)) {
    if (
      entry
      && typeof entry.count === 'number'
      && Number.isFinite(entry.count)
      && entry.count >= 0
      && entry.count <= MAX_REASONABLE_COUNT
      && typeof entry.ts === 'number'
      && Number.isFinite(entry.ts)
      && typeof entry.markerId === 'string'
      && entry.markerId.length > 0
    ) {
      m.set(roomId, { count: entry.count, ts: entry.ts, markerId: entry.markerId });
    }
  }
  return m;
}

function writeStorage(userId, rooms) {
  const storage = getStorage();
  if (!storage) return;
  try {
    const payload = { v: SCHEMA_VERSION, rooms: Object.fromEntries(rooms) };
    storage.setItem(storageKey(userId), JSON.stringify(payload));
  } catch {
    // Quota exceeded, private mode, etc. — floor is best-effort.
  }
}

function getUserCache(userId) {
  let m = cache.get(userId);
  if (!m) {
    m = parseStorage(userId);
    cache.set(userId, m);
  }
  return m;
}

function getInactiveSet(userId) {
  let set = inactiveRooms.get(userId);
  if (!set) {
    set = new Set();
    inactiveRooms.set(userId, set);
  }
  return set;
}

/**
 * @returns {number | undefined} count, or `undefined` when there is no
 * floor for this (user, room), or the floor is inactive, or the kill
 * switch is engaged.
 */
export function getFloor(userId, roomId) {
  if (isDisabled() || !userId || !roomId) return undefined;
  if (getInactiveSet(userId).has(roomId)) return undefined;
  const entry = getUserCache(userId).get(roomId);
  return entry ? entry.count : undefined;
}

/**
 * Like {@link getFloor} but returns the full `{ count, ts, markerId }`
 * tuple so the caller can compare `markerId` against the live marker.
 */
export function getFloorEntry(userId, roomId) {
  if (isDisabled() || !userId || !roomId) return undefined;
  if (getInactiveSet(userId).has(roomId)) return undefined;
  return getUserCache(userId).get(roomId);
}

/**
 * Persist a trusted unread count. Silently dropped when:
 * - kill switch engaged,
 * - args are missing or out of range,
 * - another tab has written a newer `ts` with a different count.
 *
 * Writing this also reactivates the floor if it was previously invalidated
 * for this session — a trusted resolution is authoritative.
 */
export function setFloor(userId, roomId, count, markerId) {
  if (isDisabled() || !userId || !roomId) return;
  if (typeof count !== 'number' || !Number.isFinite(count)) return;
  if (count < 0 || count > MAX_REASONABLE_COUNT) return;
  if (typeof markerId !== 'string' || !markerId) return;

  // Never persist zero. Post-refresh warm-up, the effective read marker
  // can briefly resolve to a synth receipt at the tail of the 10-event
  // initial-sync window, making `totalFromTimeline=0` a legitimate-looking
  // observation on the trusted path — which would otherwise wipe the
  // pre-refresh high-water mark. The only signal that truly means "no
  // unread" is the user actively viewing the room, which goes through
  // `clearFloorForRoom` in useReadReceipts — not through this function.
  if (count <= 0) return;

  // A positive trusted observation is authoritative; reactivate any prior
  // session-local invalidation.
  getInactiveSet(userId).delete(roomId);

  const cached = getUserCache(userId).get(roomId);

  // Fast path: exact match → nothing to persist.
  if (cached && cached.count === count && cached.markerId === markerId) {
    return;
  }

  // Unconditional monotonic non-decrease. Pre-refresh high-water mark must
  // survive the post-refresh decryption crawl (1, 3, 5 …). Legitimate
  // "count dropped to zero" goes through clearFloorForRoom (active view),
  // which bypasses setFloor entirely.
  if (cached && cached.count > count) {
    return;
  }

  // Re-read storage so we honour any write from another tab we haven't
  // yet processed via the `storage` event listener.
  const current = getStorage() ? parseStorage(userId) : getUserCache(userId);
  cache.set(userId, current);

  const our = { count, ts: Date.now(), markerId };
  const existing = current.get(roomId);

  if (existing && existing.count === count && existing.markerId === markerId) {
    return;
  }
  if (existing && existing.count > count) {
    return;
  }
  // Strict > — within-tab successive writes in the same millisecond would
  // otherwise be blocked because their timestamps are equal.
  if (
    existing
    && existing.ts > our.ts
    && (existing.count !== our.count || existing.markerId !== our.markerId)
  ) {
    // Another tab wrote a newer value — don't clobber it.
    return;
  }

  current.set(roomId, our);
  writeStorage(userId, current);
}

/**
 * Suppress the floor for this (user, room) until the session reloads or a
 * fresh `setFloor` replaces it. Does not delete the stored value — useful
 * when a receipt arrives but we want to remain authoritative if the user
 * refreshes again before another trusted resolution.
 */
export function invalidateFloor(userId, roomId) {
  if (!userId || !roomId) return;
  getInactiveSet(userId).add(roomId);
}

/**
 * Extract the maximum ts across our own user's entries in a RoomEvent.Receipt
 * content blob. Matrix receipt content is shaped as
 *   { [eventId]: { [receiptType]: { [userId]: { ts, ... } } } }
 * where receiptType is `m.read`, `m.read.private`, or `m.fully_read`.
 * Returns 0 when no own-user receipt is present.
 *
 * @param {import('matrix-js-sdk').MatrixEvent} event
 * @param {string} userId
 * @returns {number}
 */
export function extractOwnReceiptTs(event, userId) {
  if (!event || !userId) return 0;
  let content;
  try {
    content = event.getContent?.();
  } catch {
    return 0;
  }
  if (!content || typeof content !== 'object') return 0;

  let max = 0;
  for (const eventId of Object.keys(content)) {
    const types = content[eventId];
    if (!types || typeof types !== 'object') continue;
    for (const receiptType of Object.keys(types)) {
      const users = types[receiptType];
      if (!users || typeof users !== 'object') continue;
      const ours = users[userId];
      if (ours && typeof ours.ts === 'number' && Number.isFinite(ours.ts) && ours.ts > max) {
        max = ours.ts;
      }
    }
  }
  return max;
}

/**
 * Invalidate the floor ONLY if the supplied `receiptTs` is meaningfully
 * newer than the floor's own `ts`. This is the precise cross-device-read
 * signal: a receipt whose timestamp post-dates the floor write represents
 * a marker advance that happened AFTER we cached the high-water mark.
 * A synth-receipt replay during initial sync carries its original
 * server-side ts, which is always OLDER than our last pre-refresh floor
 * write — so those do not trip this check.
 *
 * Timestamp domain mismatch — known, accepted tradeoff:
 *   - `entry.ts` (stored floor ts) is client-local `Date.now()`, written
 *     by `setFloor` on this device.
 *   - `receiptTs` (incoming) is the server-origin `ts` pulled from the
 *     receipt content blob by `extractOwnReceiptTs` — i.e., the
 *     homeserver's wall clock, not ours.
 *   The comparison below therefore crosses clocks. The 1000ms (1s)
 *   epsilon is the allowed client/server skew budget: a well-behaved
 *   NTP-synced pairing easily stays under that. Beyond ±1s:
 *   - if server is AHEAD of client by >1s, a stale receipt (replay of
 *     our own pre-refresh marker) could wrongly clear the floor
 *   - if server is BEHIND client by >1s, a genuine fresh cross-device
 *     receipt could fail to clear until the next trusted resolution
 *   We accept this because realistic NTP skew is sub-second, and
 *   widening the window would delay legitimate cross-device clears by
 *   the same amount.
 *
 * Returns true iff the floor was cleared.
 */
export function maybeInvalidateFloorForReceipt(userId, roomId, receiptTs) {
  if (!userId || !roomId) return false;
  if (typeof receiptTs !== 'number' || !Number.isFinite(receiptTs)) return false;
  const entry = getFloorEntry(userId, roomId);
  if (!entry) return false;
  // Strictly greater than (with 1s skew) — equal ts means "we just wrote
  // this floor from a trusted resolution at this moment," the receipt
  // firing in the same sync tick is the echo of that same state.
  if (receiptTs > entry.ts + 1000) {
    clearFloorForRoom(userId, roomId);
    return true;
  }
  return false;
}

/**
 * Remove the stored floor for a single room.
 */
export function clearFloorForRoom(userId, roomId) {
  if (!userId || !roomId) return;
  const current = getUserCache(userId);
  if (current.delete(roomId)) {
    writeStorage(userId, current);
  }
  const set = inactiveRooms.get(userId);
  if (set) set.delete(roomId);
}

/**
 * Remove every stored floor for `userId`. Called on logout to avoid
 * cross-session leakage. Other users' floors stay intact (shared-browser).
 */
export function clearFloorsForUser(userId) {
  if (!userId) return;
  cache.delete(userId);
  inactiveRooms.delete(userId);
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(storageKey(userId));
  } catch {
    // ignore
  }
}

/**
 * Attach a `storage` event listener so cross-tab writes refresh the
 * in-memory cache for `userId`. Returns a teardown function.
 */
export function wireCrossTabSync(userId) {
  if (!userId || typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return () => {};
  }
  const key = storageKey(userId);
  const handler = (event) => {
    if (event?.key !== key) return;
    // Drop the cache; the next read re-parses storage.
    cache.delete(userId);
  };
  window.addEventListener('storage', handler);
  return () => {
    try {
      window.removeEventListener('storage', handler);
    } catch {
      // ignore
    }
  };
}

/**
 * Test-only: reset module state. Not for production callers.
 */
export function __resetFloorStateForTests() {
  cache.clear();
  inactiveRooms.clear();
}
